// Additional styled components for form and layout
import { PrimaryButton as PrimaryButtonBase } from 'components/misc/Buttons.js';
import { SectionHeading, Subheading as SubheadingBase } from 'components/misc/Headings.js';
import emailjs from 'emailjs-com';
import Image1Src from 'images/landing/1.webp';
import Image4Src from 'images/landing/4.png';
import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

// Styled components for success and error messages
const SuccessMessage = tw.div`flex items-center bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mt-4 text-sm font-semibold`;
const ErrorMessage = tw.div`flex items-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4 text-sm font-semibold`;

// Tooltip validation error message
const ValidationTooltip = styled.div`
    ${tw`absolute left-0 z-10 mt-1 px-2 py-1 text-xs text-white bg-red-500 rounded shadow-lg whitespace-nowrap`}
    top: 100%;
    &:before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 8px;
        border: 4px solid transparent;
        border-bottom-color: #ef4444;
    }
`;

// Layout styled components
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = styled(Column)`
    ${tw`md:w-5/12 flex-shrink-0 h-80 md:h-auto`}
`;
const TextColumn = styled(Column)((props) => [
    tw`md:w-7/12 mt-16 md:mt-0`,
    props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`,
]);
const Image = styled.div((props) => [
    `background-image: url("${props.imageSrc}");`,
    tw`rounded bg-contain bg-no-repeat bg-center h-1/2`,
]);
const Bold = tw.span`font-bold`;
const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-10 lg:py-12`;
// Additional styled components for form and layout
const TextContent = tw.div`lg:py-8 text-center md:text-left`;
const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(
    SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-400`;
const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`;
const Input = tw.input`w-full first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`;
const Textarea = styled(Input).attrs({ as: 'textarea' })`
    ${tw`h-24`}
`;
const SubmitButton = styled(PrimaryButtonBase)`
    ${tw`inline-block mt-8 relative flex items-center justify-center`}
    min-height: 40px;
    max-height: 40px;
    font-size: 16px;
    &:disabled {
        ${tw`opacity-50 cursor-not-allowed`}
    }
`;

// Loading spinner component
const LoadingSpinner = styled.svg`
    ${tw`animate-spin -ml-1 mr-3 h-5 w-5 text-white inline flex-shrink-0`}
`;
const Features = tw.div`mt-4 md:mt-8 max-w-sm mx-auto md:mx-0`;
const Feature = tw.div`mt-2 flex items-start md:flex-row`;
const FeatureIconContainer = styled.div`
    ${tw`mx-auto md:mx-1 inline-block text-center py-1 flex-shrink-0`}
    svg {
        ${tw`w-5 h-5 text-primary-500`}
    }
`;
const FeatureText = tw.div`w-full ml-2 text-left`;
const FeatureHeading = tw.div`font-bold text-lg text-primary-500`;
const PrimaryText = tw.span`text-primary-500`;

export default ({
    subheading = 'Kontaktirajte nas',
    heading = (
        <>
            Želite povećati prodaju kroz <PrimaryText>bolje podatke?</PrimaryText>
        </>
    ),
    description = (
        <>
            <Bold>Kontaktirajte nas – prvi korak je besplatan savet.</Bold> Za sva pitanja, sugestije ili komentare,
            slobodno nam se obratite na neki od načina ispod. Uvek smo tu da vam pomognemo i odgovorimo na vaša pitanja.
        </>
    ),
    submitButtonText = 'Pošaljite',
    textOnLeft = true,
    features = null,
}) => {
    // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });

    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const validateField = (name, value) => {
        let error = '';
        switch (name) {
            case 'name':
                if (!value.trim()) error = 'Ime je obavezno';
                else if (value.trim().length < 2) error = 'Unesite ime';
                break;
            case 'email':
                if (!value.trim()) error = 'E-mail je obavezan';
                else if (!/^\S+@\S+\.\S+$/.test(value)) error = 'Unesite ispravan e-mail';
                break;
            case 'phone':
                if (!value.trim()) error = 'Telefon je obavezan';
                else if (!/^\+?[0-9\s-]{6,}$/.test(value)) error = 'Unesite ispravan broj telefona';
                break;
            case 'subject':
                if (!value.trim()) error = 'Naslov je obavezan';
                else if (value.trim().length < 3) error = 'Naslov mora imati najmanje 3 karaktera';
                break;
            case 'message':
                if (!value.trim()) error = 'Poruka je obavezna';
                else if (value.trim().length < 10) error = 'Poruka mora imati najmanje 10 karaktera';
                break;
            default:
                break;
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error on typing
        if (fieldErrors[name]) {
            setFieldErrors({
                ...fieldErrors,
                [name]: '',
            });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        setFieldErrors({
            ...fieldErrors,
            [name]: error,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate all fields
        const errors = {};
        Object.keys(formData).forEach((key) => {
            const error = validateField(key, formData[key]);
            if (error) errors[key] = error;
        });

        setFieldErrors(errors);

        // If there are errors, don't submit
        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsLoading(true);
        setError('');

        emailjs
            .send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID, // npr. "service_xxx"
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID, // npr. "template_xxx"
                formData,
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY // npr. "abc123XYZ"
            )
            .then(
                (result) => {
                    console.log('Uspešno poslato:', result.text);
                    setSuccess(true);
                    setError('');
                    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                    setFieldErrors({});
                },
                (error) => {
                    console.error('Greška:', error.text);
                    setError('Slanje nije uspelo, pokušajte ponovo.');
                }
            )
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Container>
            <TwoColumn>
                <ImageColumn>
                    <Image imageSrc={Image1Src} />
                    <Image imageSrc={Image4Src} />
                </ImageColumn>
                <TextColumn textOnLeft={textOnLeft}>
                    <TextContent>
                        {subheading && <Subheading>{subheading}</Subheading>}
                        <Heading id="kontakt">{heading}</Heading>
                        {description && <Description>{description}</Description>}
                        <Form onSubmit={handleSubmit}>
                            <div style={{ position: 'relative' }}>
                                <Input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Vaše ime"
                                    css={fieldErrors.name ? tw`border-red-500` : undefined}
                                />
                                {fieldErrors.name && <ValidationTooltip>{fieldErrors.name}</ValidationTooltip>}
                            </div>
                            <div style={{ position: 'relative', marginTop: '1.5rem' }}>
                                <Input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Vaš e-mail"
                                    css={fieldErrors.email ? tw`border-red-500` : undefined}
                                />
                                {fieldErrors.email && <ValidationTooltip>{fieldErrors.email}</ValidationTooltip>}
                            </div>
                            <div style={{ position: 'relative', marginTop: '1.5rem' }}>
                                <Input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Vaš telefon"
                                    css={fieldErrors.phone ? tw`border-red-500` : undefined}
                                />
                                {fieldErrors.phone && <ValidationTooltip>{fieldErrors.phone}</ValidationTooltip>}
                            </div>
                            <div style={{ position: 'relative', marginTop: '1.5rem' }}>
                                <Input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Naslov"
                                    css={fieldErrors.subject ? tw`border-red-500` : undefined}
                                />
                                {fieldErrors.subject && <ValidationTooltip>{fieldErrors.subject}</ValidationTooltip>}
                            </div>
                            <div style={{ position: 'relative', marginTop: '1.5rem' }}>
                                <Textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Vaša poruka"
                                    css={
                                        fieldErrors.message
                                            ? tw`first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500 h-24 border-red-500`
                                            : undefined
                                    }
                                />
                                {fieldErrors.message && <ValidationTooltip>{fieldErrors.message}</ValidationTooltip>}
                            </div>
                            <SubmitButton type="submit" disabled={isLoading}>
                                {isLoading && (
                                    <LoadingSpinner fill="none" viewBox="0 0 24 24">
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            css={tw`opacity-25`}
                                        />
                                        <path
                                            fill="currentColor"
                                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            css={tw`opacity-75`}
                                        />
                                    </LoadingSpinner>
                                )}
                                {isLoading ? 'Šalje se...' : submitButtonText}
                            </SubmitButton>{' '}
                            {success && (
                                <SuccessMessage>
                                    <svg
                                        css={tw`text-green-500`}
                                        width="22"
                                        height="22"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                    Poruka uspešno poslata!
                                </SuccessMessage>
                            )}
                            {error && (
                                <ErrorMessage>
                                    <svg
                                        css={tw`text-red-500`}
                                        width="22"
                                        height="22"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                    {error}
                                </ErrorMessage>
                            )}
                        </Form>
                        <Features>
                            {features.map((feature, index) => (
                                <Feature key={index}>
                                    <FeatureIconContainer>{<feature.Icon />}</FeatureIconContainer>
                                    <FeatureText>
                                        <FeatureHeading>{feature.title}</FeatureHeading>
                                    </FeatureText>
                                </Feature>
                            ))}
                        </Features>
                    </TextContent>
                </TextColumn>
            </TwoColumn>
        </Container>
    );
};
